import React, { useState } from 'react';
import { Shield, Check, AlertCircle, Key, Lock } from 'lucide-react';
import { useAuth } from '../lib/authContext';

interface SetupAdminProps {
  onComplete: () => void;
}

const ADMIN_SECRET_CODE = 'Bonwayinkosi20';

const SetupAdmin: React.FC<SetupAdminProps> = ({ onComplete }) => {
  const { user } = useAuth();
  const [secretCode, setSecretCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate secret code
    if (secretCode !== ADMIN_SECRET_CODE) {
      setError('Invalid admin code. Access denied.');
      setLoading(false);
      return;
    }

    if (!user?.email) {
      setError('You must be logged in to become an admin.');
      setLoading(false);
      return;
    }

    try {
      const API_BASE = import.meta.env.PROD 
        ? 'https://izwi-api.orbxcode.workers.dev' 
        : '';

      const res = await fetch(`${API_BASE}/api/admin/elevate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: user.email,
          secret_code: secretCode 
        })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Failed to elevate to admin');
      } else {
        setSuccess(true);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (err) {
      setError('Failed to setup admin. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center">
        <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-500" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Admin Access Granted!</h2>
        <p className="text-zinc-400">Welcome to the control center. Reloading...</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-purple-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-purple-500" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Admin Access Required</h2>
        <p className="text-zinc-400">Enter the secret admin code to access the control panel.</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <Key className="absolute left-3 top-3.5 w-5 h-5 text-zinc-500" />
          <input
            type="password"
            value={secretCode}
            onChange={(e) => setSecretCode(e.target.value)}
            placeholder="Enter admin secret code"
            required
            className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-3 pl-10 text-white focus:border-purple-500 focus:outline-none"
          />
        </div>
        
        <button
          type="submit"
          disabled={loading || !secretCode}
          className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Verifying...
            </>
          ) : (
            <>
              <Lock className="w-4 h-4" />
              Unlock Admin Access
            </>
          )}
        </button>
      </form>

      <div className="mt-6 p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg">
        <p className="text-zinc-500 text-sm text-center">
          <span className="text-zinc-400 font-medium">Logged in as:</span> {user?.email || 'Not logged in'}
        </p>
      </div>

      <p className="text-zinc-600 text-xs text-center mt-4">
        Admin access is restricted. Contact the system administrator if you need access.
      </p>
    </div>
  );
};

export default SetupAdmin;
